import { ThreeToolsInstance, usePartDefinition } from "@project/emerge";
import type { AbstractPartDefinition } from "@project/emerge-types";
import { useEffect, useMemo, useRef } from "react";
import {
  EdgesGeometry,
  ExtrudeGeometry,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  SphereGeometry,
} from "three";
import type { LTPartType } from "../enum/PartType";
import {
  AluminumMaterial,
  getRawAluminumMaterial,
} from "../materials/AluminumMaterial";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";

// Definiere einen passenden Typ für den Renderer
interface AngleSectionRendererProps
  extends AbstractPartDefinition<LTPartType.AngleSection> {
  length: number;
  partSelection: AngleSectionDefinition["partSelection"];
  cuts?: AngleSectionDefinition["cuts"];
}

export const AngleSectionRenderer = ({ id }: { id: string }) => {
  // Nur die Eigenschaften abonnieren, die für das Rendering benötigt werden
  const partDef = usePartDefinition<AngleSectionRendererProps>({
    id,
    select: ["length", "partSelection", "cuts"] as const,
  });

  // Extract values with defaults (hooks must be called before early return)
  const length = partDef?.length ?? 0;
  const partSelection = partDef?.partSelection;
  const cuts = partDef?.cuts;

  // Get the dimensions from the partSelection
  const strength = partSelection?.strength || 0.2;
  const depth = partSelection?.depth || 1;
  const cutSize = partSelection?.cutSize || 0;

  // Create the horizontal rectangle shape (XZ plane) with optional cut corners
  const horizontalRectShape = useMemo(() => {
    // Define the horizontal rectangle points (in XZ plane)
    const points: Array<[number, number]> = [];

    // Start at top left
    points.push([-length / 2, depth]);

    // Bottom left - with optional cut
    if (cuts?.right?.bottom && cutSize > 0) {
      // Add a point for the cut
      points.push([-length / 2, cutSize], [-length / 2 + cutSize, 0]);
    } else {
      points.push([-length / 2, 0]);
    }

    // Top right - with optional cut
    if (cuts?.left?.bottom && cutSize > 0) {
      // Add a point for the cut
      points.push([length / 2 - cutSize, 0], [length / 2, cutSize]);
    } else {
      points.push([length / 2, 0]);
    }

    // Bottom left and close the shape
    points.push([length / 2, depth], [-length / 2, depth]);

    // Create a Shape from the points
    return ThreeToolsInstance.pointsToShape(points);
  }, [length, depth, cuts, cutSize]);

  // Create the vertical rectangle shape (XY plane) with optional cut corners
  const verticalRectShape = useMemo(() => {
    // Define the vertical rectangle points (in XY plane)
    const points: Array<[number, number]> = [];

    // Start at bottom left, then bottom right
    points.push([-length / 2, 0], [length / 2, 0]);

    // Top right - with optional cut
    if (cuts?.left?.top && cutSize > 0) {
      // Add a point for the cut
      points.push([length / 2, depth - cutSize], [length / 2 - cutSize, depth]);
    } else {
      points.push([length / 2, depth]);
    }

    // Top left - with optional cut
    if (cuts?.right?.top && cutSize > 0) {
      // Add a point for the cut
      points.push(
        [-length / 2 + cutSize, depth],
        [-length / 2, depth - cutSize],
      );
    } else {
      points.push([-length / 2, depth]);
    }

    // Close the shape
    points.push([-length / 2, 0]);

    // Create a Shape from the points
    return ThreeToolsInstance.pointsToShape(points);
  }, [length, depth, cuts, cutSize]);

  // Create the extrusion settings
  const extrudeSettings = useMemo(() => {
    return {
      steps: 1,
      depth: strength,
      bevelEnabled: false,
    };
  }, [strength]);

  // Create the horizontal part mesh
  const horizontalMesh = useMemo(() => {
    const geometry = new ExtrudeGeometry(horizontalRectShape, extrudeSettings);
    const mesh = new Mesh(geometry, getRawAluminumMaterial());

    // Position the horizontal part - match the old boxGeometry position
    mesh.position.set(0, -strength, depth - strength);
    mesh.rotation.set(-Math.PI / 2, 0, 0); // Rotate to lie flat in XZ plane

    // Add edges to the mesh
    const edges = new LineSegments(
      new EdgesGeometry(geometry),
      new LineBasicMaterial({ color: "black" }),
    );
    mesh.add(edges);

    return mesh;
  }, [horizontalRectShape, extrudeSettings, depth, strength]);

  // Create the vertical part mesh
  const verticalMesh = useMemo(() => {
    const geometry = new ExtrudeGeometry(verticalRectShape, extrudeSettings);
    const mesh = new Mesh(geometry, getRawAluminumMaterial());

    // Position the vertical part - match the old boxGeometry position
    mesh.position.set(0, -strength, -strength);

    // Add edges to the mesh
    const edges = new LineSegments(
      new EdgesGeometry(geometry),
      new LineBasicMaterial({ color: "black" }),
    );
    mesh.add(edges);

    return mesh;
  }, [verticalRectShape, extrudeSettings, strength]);

  // Early return after all hooks
  if (!partDef) return null;

  // Calculate offset to move rotation point to inner corner of the L
  // The inner corner is at (0,0,0) in local coordinates after adjusting the position
  const offsetX = 0;
  const offsetY = 0;
  const offsetZ = 0;

  // Final group position calculation - ohne Shift, da dieser bereits in der Position berücksichtigt ist
  const groupPosition: [number, number, number] = [
    -offsetX,
    -offsetY,
    -offsetZ,
  ];

  return (
    <group
      // Adjust position to move rotation point to inner corner
      position={[offsetX, offsetY, offsetZ]}
    >
      {/*
        The additional group is needed to apply the base orientation.
        This matches the old implementation's hierarchy where it creates a newEmergeOrientation group
        We need to offset the inner group to compensate for the outer group's offset
      */}
      <group rotation={[Math.PI, 0, 0]} position={groupPosition}>
        {/* Horizontal part */}
        <primitive object={horizontalMesh} />

        {/* Vertical part */}
        <primitive object={verticalMesh} />

        {/* Render rivets */}
        {renderRivets(length, depth, strength)}
      </group>
    </group>
  );
};

import { calculateRivetPositions } from "@project/pricing";

const RivetPair = ({
  xPosition,
  depth,
  strength,
  keyPrefix,
}: {
  xPosition: number;
  depth: number;
  strength: number;
  keyPrefix: string;
}) => {
  const mesh1Ref = useRef<Mesh>(null);
  const mesh2Ref = useRef<Mesh>(null);
  const sphereGeometry = useMemo(() => new SphereGeometry(4, 8, 8), []);

  useEffect(() => {
    if (mesh1Ref.current) {
      mesh1Ref.current.geometry = sphereGeometry;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.geometry = sphereGeometry;
    }
  }, [sphereGeometry]);

  return (
    <group key={keyPrefix}>
      <mesh
        ref={mesh1Ref}
        position={[xPosition, -strength, depth / 2]}
        scale={[1, 0.3, 1]}
      >
        <AluminumMaterial />
      </mesh>
      <mesh
        ref={mesh2Ref}
        position={[xPosition, depth / 2, -strength]}
        scale={[1, 1, 0.3]}
      >
        <AluminumMaterial />
      </mesh>
    </group>
  );
};

const renderRivets = (length: number, depth: number, strength: number) => {
  const positions = calculateRivetPositions(length);
  if (positions.length === 0) return null;
  return positions.map((xPos) => (
    <RivetPair
      key={`rivet-${xPos}`}
      xPosition={xPos}
      depth={depth}
      strength={strength}
      keyPrefix={`rivet-${xPos}`}
    />
  ));
};
